import React, { useContext, useState, useCallback, useMemo, memo } from 'react';
import {TouchableOpacity, StyleSheet, View, Alert, Platform, Modal, Animated, Text, TouchableHighlight} from 'react-native';
import {Ionicons, MaterialIcons} from '@expo/vector-icons';
import Feather from '@expo/vector-icons/Feather';
import { CartContext } from '../helpers/CartContext';
import useConfirmNavigation from '../helpers/UseConfirmNavigation';
import { TableContext } from "../helpers/TableContext";
import { OnlineOrderContext } from "../helpers/OnlineOrderContext";
import { useTranslation } from 'react-i18next';
import '../../i18n';

const CircularButton = ({ navigation, iconName, style }) => {
    const { t } = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);
    const { cartItems, setAlreadySentCartItems } = useContext(CartContext);
    const { removeTable } = useContext(TableContext);
    const { orderData, updateOrderData, resetOnlineOrder } = useContext(OnlineOrderContext);
    const confirmNavigation = useConfirmNavigation(navigation);

    // Use useCallback to memoize the handleSelect function
    const handleSelect = useCallback((value) => {
        setModalVisible(false);
        const currentRoute = navigation.getState().routes[navigation.getState().index].name;

        const navigateToScreen = (screen) => {
            if (cartItems.length > 0) {
                confirmNavigation(screen);
            } else {
                removeTable();
                setAlreadySentCartItems([]);
                resetOnlineOrder();
                updateOrderData({
                    // Reset other order data fields as needed
                }, '');
                navigation.navigate(screen);
            }
        };

        const showAlert = () => {
            if (Platform.OS === 'web') {
                if (window.confirm(t("home_delivery_alert"))) {
                    updateOrderData({
                        // Reset other order data fields as needed
                    }, '');
                    navigateToScreenBasedOnValue(value, currentRoute, navigateToScreen);
                }
            } else {
                Alert.alert(
                    t("attention"),
                    t("home_delivery_alert"),
                    [
                        {
                            text: t("cancel"),
                            style: "cancel"
                        },
                        {
                            text: t("yes"),
                            onPress: () => {
                                updateOrderData({
                                    userInfo: null,
                                    addressInfo: null,
                                    // Reset other order data fields as needed
                                },'');
                                navigateToScreenBasedOnValue(value, currentRoute, navigateToScreen);
                            }
                        }
                    ]
                );
            }
        };

        const navigateToScreenBasedOnValue = (value, currentRoute, navigateToScreen) => {
            if (value === 'takeAway') {
                if (currentRoute !== 'HomeScreen') {
                    navigateToScreen('HomeScreen');
                }
            } else if (value === 'dineIn') {
                if (currentRoute !== 'TablesScreen') {
                    navigateToScreen('TablesScreen');
                }
            } else if (value === 'delivery') {
                if (currentRoute !== 'OnlineScreen') {
                    navigateToScreen('OnlineScreen');
                }
            }
        };

        if (orderData?.userInfo && orderData?.addressInfo) {
            showAlert();
        } else {
            navigateToScreenBasedOnValue(value, currentRoute, navigateToScreen);
        }
    }, [navigation, cartItems, confirmNavigation, removeTable, setAlreadySentCartItems, resetOnlineOrder, updateOrderData, orderData, t]);

    const scaleValue = React.useRef(new Animated.Value(1)).current;

    // Memoize animation functions to prevent recreation on each render
    const handleMouseEnter = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1.4,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    const handleMouseLeave = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    const handlePressIn = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1.4,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    const handlePressOut = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
        }).start();
        setModalVisible(true);
    }, [scaleValue]);

    // Memoize the button style to prevent recreation on each render
    const buttonStyle = useMemo(() => [
        styles.circularButton,
        style,
        { transform: [{ scale: scaleValue }]},
    ], [style, scaleValue]);

    return (
        <View style={styles.menuContainer}>
            <TouchableOpacity
                activeOpacity={0.8}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onPressIn={handlePressIn}
                onPressOut={handlePressOut}
                style={buttonStyle}
            >
                <Ionicons name={iconName} size={30} style={styles.icon} />
            </TouchableOpacity>

            <Modal
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <TouchableOpacity
                    style={styles.modalOverlay}
                    activeOpacity={1}
                    onPressOut={() => setModalVisible(false)}
                >
                    {/* Memoize the modal content to prevent unnecessary re-renders */}
                    {useMemo(() => (
                        <View style={styles.modalContent}>
                            <TouchableHighlight 
                                activeOpacity={0.9} 
                                underlayColor={"#ffefc1"} 
                                onPress={() => handleSelect('takeAway')} 
                                style={styles.modalItem}
                            >
                                <View style={styles.buttonRow}>
                                    <Ionicons name="bag-outline" size={30} color="#000" />
                                    <Text style={[styles.modalText,{color: "#000"}]}>{t("quick_receipt")}</Text>
                                </View>
                            </TouchableHighlight>
                            <TouchableHighlight 
                                activeOpacity={0.9} 
                                underlayColor={'#ffefc1'} 
                                onPress={() => handleSelect('dineIn')} 
                                style={styles.modalItem}
                            >
                                <View style={styles.buttonRow}>
                                    <MaterialIcons name="table-bar" size={30} color="#F1B44C" />
                                    <Text style={[styles.modalText,{color: "#F1B44C"}]}>{t("table_map")}</Text>
                                </View>
                            </TouchableHighlight>
                            <TouchableHighlight 
                                activeOpacity={0.9} 
                                underlayColor={'#ffefc1'} 
                                onPress={() => handleSelect('delivery')} 
                                style={styles.modalItem}
                            >
                                <View style={styles.buttonRow}>
                                    <Feather name="truck" size={30} color="#50A5F1" />
                                    <Text style={[styles.modalText,{color: "#50A5F1"}]}>{t("home_delivery")}</Text>
                                </View>
                            </TouchableHighlight>
                        </View>
                    ), [t, handleSelect])}
                </TouchableOpacity>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    buttonRow: {

        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 20,
        width: '100%',
    },
    circularButton: {
        margin: "auto",
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: '#F1B34B',
        justifyContent: 'center',
        alignItems: 'center',
        elevation: 8,
        zIndex: 999, // Ensure the button is above other elements
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 3,
        },
        shadowOpacity: 0.27,
        shadowRadius: 4.65,

        elevation: 6,

    },
    menuContainer: {
        position: 'absolute',
        bottom: 40,
        left: '50%',
        transform: [{ translateX: -25 }],
        zIndex: 999, // Ensure the menu is above other elements
    },
    modalOverlay: {
        flex: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    modalContent: {
        width: 280,
        backgroundColor: '#fff',
        borderRadius: 10,
        padding: 15,
        marginTop: 15,

        alignItems: 'center',
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 7,
        },
        shadowOpacity: 0.30,
        shadowRadius: 7,
        elevation: 3,
        marginBottom: 70, // Position the modal above the circular button
    },
    modalItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 17,
        width: '100%',
        borderBottomColor: '#EEE',
        borderBottomWidth: 0.7,

        paddingTop: 12,
        paddingBottom: 12,

        textAlign: "center",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    modalText: {
        marginLeft: 35,
        fontSize: 22,
        fontWeight: '900',
        textAlign: 'center',
    },
    icon: {
        color: '#fff',
    },
});

export default memo(CircularButton);
